import { CommonModule, Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';
import { TableModule } from 'primeng/table';
import { CalendarModule } from 'primeng/calendar';
import { ButtonModule } from 'primeng/button';
import { CallService } from 'src/app/core/services/call.service';
import {
  Call,
  CallData,
  CallFilter,
  CallFilterParams,
  CallResponse,
} from 'src/app/shared/model/call.model';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { ToastModule } from 'primeng/toast';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { CommonService } from 'src/app/shared/services/common.service';
import { TABLE_PARAM, STATUSES } from 'src/app/shared/constants/common.constants';
import httpErrorCodes from 'src/app/core/common/http_error_codes';
import { domainUrl } from 'src/environments/environment';
import { PaginationService } from 'src/app/shared/providers/pagination/pagination.service';

@Component({
  selector: 'app-call',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    FormsModule,
    HttpClientModule,
    TableModule,
    ButtonModule,
    CalendarModule,
    ToastModule,
  ],
  providers: [CallService, TranslateService],
  templateUrl: './calls.component.html',
  styleUrls: ['./calls.component.scss'],
})
export class CallsComponent implements OnInit {
  tableParams = { ...TABLE_PARAM };

  callItems: Call[] = [];

  noDataRow = [{}];

  totalRecords = 0;

  first = 1;

  rows = TABLE_PARAM.PAGE_SIZE;

  sortField = 'CreatedOn';

  sortOrder = TABLE_PARAM.SORT_DEC_ORDER;

  callPageNumber: CallData['pageNumber'] = TABLE_PARAM.PAGE_NUMBER;

  callPageSize: CallData['pageSize'] = TABLE_PARAM.PAGE_SIZE;

  filters: CallFilter = {
    dateRange: null,
  };

  baseApiUrl = domainUrl.url;

  filePath: CallResponse['filePath'];

  constructor(
    private callService: CallService,
    private toastService: ToasterService,
    private translateService: TranslateService,
    private router: Router,
    private location: Location,
    private paginationService: PaginationService,
  ) {}

  ngOnInit() {
    this.rows = this.paginationService.getPageSize('call');
    this.tableParams.ROW_PER_PAGE = this.paginationService.getAllowedSizes();
    this.callPageSize = this.rows;
    this.callPageNumber = 1;
    this.loadCalls(this.callPageNumber, this.callPageSize, this.sortField, this.sortOrder);
  }

  loadCalls(
    pageNumber: number,
    pageSize: number,
    sortField: string,
    sortOrder: string,
    isExport?: boolean,
  ) {
    const formattedDates =
      this.filters?.dateRange?.map((date) => CommonService.formatDate(date?.toString())) || [];

    const isValidDate = (date: string) => date && !date.includes('NaN');
    const startDate = isValidDate(formattedDates[0]) ? formattedDates[0] : '';
    const endDate = isValidDate(formattedDates[1]) ? formattedDates[1] : '';

    const callFilterParams: CallFilterParams = {
      pageNumber,
      pageSize,
      createdFrom: startDate,
      createdTo: endDate,
      sortField,
      sortOrder,
      isExport,
    };
    this.callService.getFilteredCallData(callFilterParams).subscribe({
      next: (response) => {
        if (response.status === STATUSES.SUCCESS) {
          if (isExport === true) {
            this.filePath = this.baseApiUrl + response.filePath;

            if (this.filePath && response.filePath) {
              const fileName = this.filePath.split('/').pop() || '';
              CommonService.downloadFile(this.filePath, fileName);
            } else {
              this.toastService.showErrorToast(
                this.translateService.instant('CALL_PAGE.TOAST_MESSAGE.FAILED_TO_EXPORT_CALL_DATA'),
              );
            }
          } else {
            this.callItems = response.data.items;
            this.totalRecords = response.data.totalCount;
          }
        }
      },
      error: (err) => {
        if (err.status !== httpErrorCodes.NOT_FOUND) {
          this.toastService.showErrorToast(
            this.translateService.instant('CALL_PAGE.TOAST_MESSAGE.FAILED_TO_LOAD_CALL_DATA'),
          );
        }
      },
    });
  }

  onResetFilters() {
    this.filters = { dateRange: null };
    this.callPageNumber = 1;
    this.applyFilters();
  }

  getConvertedDate(utcDate: string) {
    return CommonService.getFormattedDate(utcDate);
  }

  applyFilters() {
    this.first = 0;
    this.callPageNumber = this.callPageNumber === 0 ? 1 : this.callPageNumber;
    this.loadCalls(this.callPageNumber, this.callPageSize, this.sortField, this.sortOrder);
  }

  pageChange(event: { first: number; rows: number }) {
    this.first = event.first;
    this.rows = event.rows;
    this.paginationService.setPageSize('call', this.rows);
    this.callPageSize = this.rows;
    this.callPageNumber = this.first / this.rows;
    this.callPageSize = this.rows;
    this.loadCalls(this.callPageNumber + 1, this.rows, this.sortField, this.sortOrder);
  }

  sortChange(event: { field: string; order: string }) {
    this.sortField = event.field;
    this.sortOrder =
      String(event.order) === '1' ? TABLE_PARAM.SORT_ASC_ORDER : TABLE_PARAM.SORT_DEC_ORDER;
    this.loadCalls(this.callPageNumber, this.callPageSize, this.sortField, this.sortOrder);
  }

  formatDuration(duration: string): string {
    if (!duration) return duration;

    // Split the duration into hours, minutes, and seconds
    const [hours, minutes, seconds] = duration.split(':');

    // If hours is 0, display only minutes and seconds
    if (parseInt(hours, 10) === 0) {
      return `${minutes}:${seconds}`;
    }

    return `${hours}:${minutes}:${seconds}`;
  }

  onLinkClick(callId: string): void {
    const tree = this.router.createUrlTree(['/call-details', callId]);
    const url = this.router.serializeUrl(tree);
    const fullUrl = window.location.origin + this.location.prepareExternalUrl(url);

    window.open(fullUrl, '_blank');
  }

  exportCallData() {
    this.loadCalls(this.callPageNumber, this.callPageSize, this.sortField, this.sortOrder, true);
    this.toastService.showSuccessToast(
      this.translateService.instant('CALL_PAGE.TOAST_MESSAGE.SUCCESS_TO_EXPORT_CALL_DATA'),
    );
  }
}
